import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  MenuItem,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { ArrowLeft, Plus, Trash2, Save, Send } from 'lucide-react';
import { useAppStore } from '../../../lib/store';
import api from '../../../lib/api';

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  vat_amount: number;
  total_with_vat: number;
}

export default function InvoiceCreate() {
  const { t } = useTranslation(['finance', 'common']);
  const navigate = useNavigate();
  const { user } = useAppStore();

  // Form state
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [customerType, setCustomerType] = useState<'tenant' | 'client'>('tenant');
  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [properties, setProperties] = useState<any[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentTerms, setPaymentTerms] = useState('30');
  const [dueDate, setDueDate] = useState('');
  const [status, setStatus] = useState<'draft' | 'sent'>('draft');
  const [includeVat, setIncludeVat] = useState(true);
  const [vatRate, setVatRate] = useState(15);
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([
    {
      id: '1',
      description: '',
      quantity: 1,
      unit_price: 0,
      line_total: 0,
      vat_amount: 0,
      total_with_vat: 0,
    },
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Calculate due date when issue date or payment terms change
  useEffect(() => {
    if (issueDate && paymentTerms) {
      const issue = new Date(issueDate);
      const due = new Date(issue);
      due.setDate(due.getDate() + parseInt(paymentTerms));
      setDueDate(due.toISOString().split('T')[0]);
    }
  }, [issueDate, paymentTerms]);

  const loadInitialData = async () => {
    try {
      // Generate invoice number
      const invoiceNum = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
      setInvoiceNumber(invoiceNum);

      // Load tenants
      const tenantsResponse = await api.profiles.getTenants();
      if (tenantsResponse.data) {
        setTenants(tenantsResponse.data);
      }

      // Load properties
      const propertiesResponse = await api.properties.getAll();
      if (propertiesResponse.data) {
        setProperties(propertiesResponse.data);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load initial data');
    }
  };

  const calculateLineItem = (item: Partial<LineItem>): LineItem => {
    const quantity = item.quantity || 0;
    const unitPrice = item.unit_price || 0;
    const lineTotal = quantity * unitPrice;
    const vatAmount = includeVat ? (lineTotal * vatRate) / 100 : 0;
    const totalWithVat = lineTotal + vatAmount;

    return {
      id: item.id || Date.now().toString(),
      description: item.description || '',
      quantity,
      unit_price: unitPrice,
      line_total: lineTotal,
      vat_amount: vatAmount,
      total_with_vat: totalWithVat,
    };
  };

  // Recalculate all line items when VAT settings change
  useEffect(() => {
    setLineItems(prevItems => prevItems.map(item => calculateLineItem(item)));
  }, [includeVat, vatRate]);

  const addLineItem = () => {
    const newItem = calculateLineItem({
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unit_price: 0,
    });
    setLineItems([...lineItems, newItem]);
  };

  const removeLineItem = (id: string) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((item) => item.id !== id));
    }
  };

  const updateLineItem = (id: string, field: keyof LineItem, value: any) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          return calculateLineItem(updated);
        }
        return item;
      })
    );
  };

  // Calculate totals
  const subtotal = lineItems.reduce((sum, item) => sum + item.line_total, 0);
  const totalVat = lineItems.reduce((sum, item) => sum + item.vat_amount, 0);
  const grandTotal = lineItems.reduce((sum, item) => sum + item.total_with_vat, 0);

  const validateForm = (): boolean => {
    if (!selectedTenantId) {
      setError('Please select a tenant');
      return false;
    }

    if (!lineItems.some((item) => item.description && item.quantity > 0 && item.unit_price > 0)) {
      setError('Please add at least one valid line item');
      return false;
    }

    return true;
  };

  const handleSubmit = async (sendNow: boolean = false) => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Note: invoices schema only has: id, invoice_number, property_id, tenant_id, amount, vat_amount,
      //       total_amount, issue_date, due_date, status, description, created_at, updated_at
      // Columns NOT in schema: notes, tax_rate, payment_terms
      const invoiceData = {
        invoice_number: invoiceNumber,
        tenant_id: selectedTenantId,
        property_id: selectedPropertyId || null,
        amount: subtotal,
        vat_amount: totalVat,
        total_amount: grandTotal,
        issue_date: issueDate,
        due_date: dueDate,
        status: sendNow ? 'sent' : 'draft',
        // Include notes in description if provided
        description: [
          lineItems.map((item) => item.description).filter(Boolean).join(', '),
          notes ? `Notes: ${notes}` : '',
        ].filter(Boolean).join(' | '),
      };

      const response = await api.invoices.create(invoiceData);

      if (response.error) {
        throw new Error(response.error.message || 'Failed to create invoice');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard/finance/invoices');
      }, 1500);
    } catch (err: any) {
      console.error('Error creating invoice:', err);
      setError(err.message || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/dashboard/finance/invoices')}>
          <ArrowLeft size={24} />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {t('createInvoice', 'Create Invoice')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('createInvoiceSubtitle', 'Create a new VAT-compliant invoice')}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('invoiceCreatedSuccess', 'Invoice created successfully!')}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Invoice Header */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {includeVat ? t('vatInvoice', 'VAT Invoice') : t('invoice', 'Invoice')}
                </Typography>
                <Chip
                  label={includeVat ? t('ksaVatCompliant', 'KSA VAT Compliant') : t('vatExempt', 'VAT Exempt')}
                  color={includeVat ? 'success' : 'default'}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('invoiceNumber', 'Invoice Number')}
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('status', 'Status')}
                    value={status}
                    onChange={(e) => setStatus(e.target.value as 'draft' | 'sent')}
                  >
                    <MenuItem value="draft">{t('invoiceStatuses.draft', 'Draft')}</MenuItem>
                    <MenuItem value="sent">{t('invoiceStatuses.sent', 'Sent')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeVat}
                        onChange={(e) => setIncludeVat(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t('includeVat', 'Include VAT')}
                  />
                </Grid>
                {includeVat && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label={t('vatRate', 'VAT Rate')}
                      value={vatRate}
                      onChange={(e) => setVatRate(Number(e.target.value))}
                    >
                      <MenuItem value={0}>{t('vatRates.0', '0% (Exempt)')}</MenuItem>
                      <MenuItem value={5}>{t('vatRates.5', '5%')}</MenuItem>
                      <MenuItem value={15}>{t('vatRates.15', '15% (Standard)')}</MenuItem>
                    </TextField>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('customerInformation', 'Customer Information')}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    required
                    label={t('selectTenant', 'Select Tenant')}
                    value={selectedTenantId}
                    onChange={(e) => setSelectedTenantId(e.target.value)}
                  >
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.first_name} {tenant.last_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('relatedProperty', 'Related Property (Optional)')}
                    value={selectedPropertyId}
                    onChange={(e) => setSelectedPropertyId(e.target.value)}
                  >
                    <MenuItem value="">{t('none', 'None')}</MenuItem>
                    {properties.map((property) => (
                      <MenuItem key={property.id} value={property.id}>
                        {property.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice Details */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('invoiceDetails', 'Invoice Details')}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t('issueDate', 'Issue Date')}
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ lang: "en-US" }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label={t('paymentTerms', 'Payment Terms')}
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                  >
                    <MenuItem value="15">15 {t('days', 'Days')}</MenuItem>
                    <MenuItem value="30">30 {t('days', 'Days')}</MenuItem>
                    <MenuItem value="60">60 {t('days', 'Days')}</MenuItem>
                    <MenuItem value="90">90 {t('days', 'Days')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t('dueDate', 'Due Date')}
                    value={dueDate}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ lang: "en-US" }}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label={t('notes', 'Notes (Optional)')}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t('lineItems', 'Line Items')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Plus size={20} />}
                  onClick={addLineItem}
                >
                  {t('addItem', 'Add Item')}
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('description', 'Description')}</TableCell>
                      <TableCell align="right">{t('quantity', 'Quantity')}</TableCell>
                      <TableCell align="right">{t('unitPrice', 'Unit Price')}</TableCell>
                      <TableCell align="right">{t('lineTotal', 'Line Total')}</TableCell>
                      {includeVat && <TableCell align="right">VAT ({vatRate}%)</TableCell>}
                      <TableCell align="right">{includeVat ? t('totalWithVat', 'Total with VAT') : t('totalAmount', 'Total')}</TableCell>
                      <TableCell align="center">{t('actions', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={item.description}
                            onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                            placeholder={t('enterDescription', 'Enter description')}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ width: 100 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 1 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ width: 120 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell align="right">{formatCurrency(item.line_total)}</TableCell>
                        {includeVat && <TableCell align="right">{formatCurrency(item.vat_amount)}</TableCell>}
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(item.total_with_vat)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="error"
                            onClick={() => removeLineItem(item.id)}
                            disabled={lineItems.length === 1}
                          >
                            <Trash2 size={20} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ width: 300 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{t('subtotal', 'Subtotal')}:</Typography>
                    <Typography>{formatCurrency(subtotal)}</Typography>
                  </Box>
                  {includeVat && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>{t('vatAmount', 'VAT')} ({vatRate}%):</Typography>
                      <Typography>{formatCurrency(totalVat)}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {t('grandTotal', 'Grand Total')}:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {formatCurrency(grandTotal)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard/finance/invoices')}
              disabled={loading}
            >
              {t('common:cancel', 'Cancel')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Save size={20} />}
              onClick={() => handleSubmit(false)}
              disabled={loading}
            >
              {t('saveAsDraft', 'Save as Draft')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Send size={20} />}
              onClick={() => handleSubmit(true)}
              disabled={loading}
            >
              {loading ? t('creating', 'Creating...') : t('createAndSend', 'Create & Send')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
